// The confirmation of a relationship delete (SEP-244.4).
//
// Two surfaces show it: the per-record `RelationshipsPanel` and the book-level
// `BookRelationshipsDirectory`. Both put Delete beside End, and the two are
// different actions. End keeps the row as history. Delete removes the row, and
// the firm keeps no copy of it. The dialog says that difference, so a person
// who wanted End does not press Delete.
//
// AN ENDED ROW READS DIFFERENTLY. Delete applies to a current row and to an
// ended one. On an ended row the person no longer has the End choice, so the
// second sentence would be advice about a choice that is gone. The `ended`
// prop selects the wording that names what the delete actually removes: the
// row and the history it holds.

import { Button, Modal } from '@/components/ui';

export interface RelationshipDeleteDialogProps {
    open: boolean;
    /** The two ends, for example "Alice Hartley and Ruth Calder". Null when
     *  the surface cannot name both ends. */
    subject: string | null;
    /** True when the relationship already ended. The wording changes, because
     *  the person no longer has the End choice. */
    ended: boolean;
    /** True while the delete is in flight. */
    busy: boolean;
    /** The reason the last try failed, or null when there was none. */
    error: string | null;
    onClose: () => void;
    onConfirm: () => void;
}

export default function RelationshipDeleteDialog({
    open,
    subject,
    ended,
    busy,
    error,
    onClose,
    onConfirm,
}: RelationshipDeleteDialogProps) {
    if (!open) return null;

    return (
        <Modal
            isOpen={open}
            onClose={onClose}
            title="Delete relationship"
            size="md"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        busy={busy}
                        onClick={onConfirm}
                        data-testid="relationship-delete-confirm"
                    >
                        Delete relationship
                    </Button>
                </>
            }
        >
            <div className="space-y-3">
                <p className="text-body-medium text-[rgb(var(--color-text-primary))]">
                    {subject
                        ? `Delete the relationship between ${subject}?`
                        : 'Delete this relationship?'}
                </p>
                <p className="text-annotation text-[rgb(var(--color-text-secondary))]">
                    {ended
                        ? 'This relationship already ended, and the row holds that history. Delete removes the row and the history, and the firm keeps no copy of them.'
                        : 'End keeps the relationship as history. Delete removes the row, and the firm keeps no copy of it.'}
                </p>
                <p className="text-annotation text-[rgb(var(--color-text-secondary))]">
                    The audit trail records the delete and the person who did it.
                </p>

                {error && (
                    <p
                        role="alert"
                        className="rounded-card border border-[rgb(var(--color-rag-red))] bg-[rgb(var(--color-rag-red)/0.08)] px-3 py-2 text-annotation text-[rgb(var(--color-text-primary))]"
                    >
                        {error}
                    </p>
                )}
            </div>
        </Modal>
    );
}
